// Vendors
import { isWebViewPlatform } from '@openbank/web-ui-framework';
import { useLocation, useNavigate } from 'react-router-dom-v6';
import { sessionUtils } from '@openbank/cf-ui-architecture-core';
import { useCallback, useEffect, useState } from 'react';
import { StorageCookiesEnum } from '@openbank/cf-ui-static-data';
// Constants
import { NOTCH_ZERO } from './constants/mobile-connector.hook.constants';
// Enumerations
import { RoutesCommonEnumeration } from 'enumerations/routes.common.enumeration';
import { RoutesEnumeration } from 'enumerations/routes.enumeration';
// Handlers
import MobileConnectorHandlers from '../handlers/mobile-connector.provider.handlers';
// Types
import { MobileConnectorHookReturnPropsType } from './types/mobile-connector-hook-return.type';
// Utilities
import {
  getAccessToken,
  getDeviceInfo,
  getUserName,
  setAccessToken,
  initMobileCommunicator,
  refreshToken,
  openWebViewBrowserLink,
} from 'utils/mobile-connector';
import { getEnvironmentDomain } from 'utils/app.utils';
import { initTealium } from './utils/mobile-connector.hook.utils';

const MobileConnectorHook = (): MobileConnectorHookReturnPropsType => {
  const [notchSize, setNotchSize] = useState(NOTCH_ZERO);
  const [loading, setLoading] = useState<boolean>(true);
  const [hybridFlowId, setHybridFlowId] = useState<string | undefined>();

  const location = useLocation();
  const isInCreditLimitIncrease = location.pathname.includes(
    RoutesCommonEnumeration.CREDIT_LIMIT_INCREASE
  );
  const isPublicFlow = location.pathname.includes(RoutesEnumeration.RECOVERY_KEY);
  const isHybrid = isWebViewPlatform();

  initTealium(isHybrid);

  const {
    handleNavigateHybridFlow: onNavigateHybridFlow,
    handleRefreshToken: onRefreshToken,
    handleHybridFlow: onHybridFlow,
    handleSendToMobileAccessToken,
    handleOpenExternalUrlOnHybridFlow,
  } = MobileConnectorHandlers({
    idFlow: hybridFlowId,
    isHybrid,
    navigate: useNavigate(),
    setHybridFlowId,
    setAccessToken,
  });

  const saveSessionInfo = async () => {
    const accessToken = await getAccessToken();
    const refreshTokenValue = (await refreshToken()) || '';
    try {
      const mobileUsername = (await getUserName()) || '{username: {}}',
        username = JSON.parse(mobileUsername)?.username;
      localStorage.setItem(StorageCookiesEnum.USERNAME, username);
    } catch (e) {
      localStorage.setItem(StorageCookiesEnum.USERNAME, '');
    }

    if (accessToken) {
      sessionUtils.saveSessionData({
        accessToken,
        refreshToken: refreshTokenValue,
        expiresIn: 100000,
        domain: getEnvironmentDomain(),
      });
    }
  };

  const getBasicInfo = useCallback(async () => {
    const initialized = await initMobileCommunicator();
    if (initialized) {
      if (!isPublicFlow) {
        saveSessionInfo();
      }
      getDeviceInfo().then((value: any) => {
        const deviceInfo = JSON.parse(value);
        const notchSize = deviceInfo.top_margin || deviceInfo.notch || NOTCH_ZERO;
        setNotchSize(notchSize);
      });
    } else {
      throw Error('initMobileCommunicator not initialized');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (isHybrid) {
        await getBasicInfo();
      }
      setLoading(false);
    };

    fetchData();
  }, [getBasicInfo, isHybrid]);

  return {
    fetching: loading && isHybrid,
    isHybrid,
    isInCreditLimitIncrease,
    notchSize,
    onNavigateHybridFlow,
    onHybridFlow,
    onRefreshToken,
    handleOpenWebViewBrowser: openWebViewBrowserLink,
    handleOpenExternalUrlOnHybridFlow,
    handleSendToMobileAccessToken,
  };
};

export default MobileConnectorHook;
